import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Backdrop,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import {
  EmojiEvents,
  TrendingDown,
  Whatshot,
} from '@mui/icons-material';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiEndpoint } from '../configuration/api';

type MetricType = 'wins' | 'avgScore' | 'streak';
type PeriodType = 'weekly' | 'monthly' | 'yearly' | 'alltime';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  value: number;
  isCurrentUser?: boolean;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  metric: MetricType;
  period: PeriodType;
  periodValue: string;
  totalEntries: number;
}

const Leaderboard: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuthGen2();
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<MetricType>('wins');
  const [period, setPeriod] = useState<PeriodType>('alltime');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasDisplayName, setHasDisplayName] = useState<boolean>(true);

  // Load leaderboard data whenever metric or period changes
  useEffect(() => {
    loadLeaderboard();
  }, [metric, period, isAuthenticated]);

  // Check if user has a display name
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      checkDisplayName();
    }
  }, [isAuthenticated, authLoading]);

  const checkDisplayName = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) return;

      const apiUrl = buildApiEndpoint('/user-settings', true);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setHasDisplayName(!!data.settings.displayName);
        }
      }
    } catch (error) {
      console.error('Error checking display name:', error);
    }
  };

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build the API URL with query parameters
      // Use authenticated endpoint if logged in (gets up to 100 results), public endpoint otherwise (gets top 10)
      const apiUrl = buildApiEndpoint(`/leaderboard?metric=${metric}&period=${period}`, isAuthenticated);

      // Prepare headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add authentication if user is logged in
      if (isAuthenticated) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setLeaderboardData(data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricChange = (event: React.SyntheticEvent, newMetric: MetricType | null) => {
    if (newMetric !== null) {
      setMetric(newMetric);
    }
  };

  const handlePeriodChange = (event: React.SyntheticEvent, newPeriod: PeriodType) => {
    setPeriod(newPeriod);
  };

  const getMetricIcon = (metricType: MetricType) => {
    switch (metricType) {
      case 'wins':
        return <EmojiEvents sx={{ mr: 1 }} />;
      case 'avgScore':
        return <TrendingDown sx={{ mr: 1 }} />;
      case 'streak':
        return <Whatshot sx={{ mr: 1 }} />;
    }
  };

  const getMetricLabel = (metricType: MetricType) => {
    switch (metricType) {
      case 'wins':
        return 'Most Wins';
      case 'avgScore':
        return 'Lowest Average Score';
      case 'streak':
        return 'Longest Win Streak';
    }
  };

  const formatValue = (metricType: MetricType, value: number) => {
    switch (metricType) {
      case 'wins':
        return `${value} ${value === 1 ? 'win' : 'wins'}`;
      case 'avgScore':
        return value.toFixed(1);
      case 'streak':
        return `${value} ${value === 1 ? 'game' : 'games'}`;
    }
  };

  const getPeriodLabel = (periodType: PeriodType) => {
    switch (periodType) {
      case 'weekly':
        return 'This Week';
      case 'monthly':
        return 'This Month';
      case 'yearly':
        return 'This Year';
      case 'alltime':
        return 'All Time';
    }
  };

  // Show loading state
  if (loading && !leaderboardData) {
    return (
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={true}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiEvents sx={{ fontSize: 40, color: 'gold', mr: 2 }} />
          <Typography variant="h4" component="h1">
            Leaderboard
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Compete with other players and see how you rank!
        </Typography>
      </Paper>

      {/* Display Name Prompt for authenticated users without a display name */}
      {isAuthenticated && !hasDisplayName && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" href="/settings">
              Set Display Name
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Set your display name in Settings to appear on the leaderboard!
        </Alert>
      )}

      {/* Metric Selection */}
      <Paper elevation={3} sx={{ mb: 2 }}>
        <Tabs
          value={metric}
          onChange={handleMetricChange}
          variant="fullWidth"
        >
          <Tab
            icon={<EmojiEvents />}
            label="Most Wins"
            value="wins"
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            icon={<TrendingDown />}
            label="Lowest Avg Score"
            value="avgScore"
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
          <Tab
            icon={<Whatshot />}
            label="Longest Streak"
            value="streak"
            iconPosition="start"
            sx={{ minHeight: 64 }}
          />
        </Tabs>
      </Paper>

      {/* Period Selection */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          aria-label="time period"
          color="primary"
        >
          <ToggleButton value="weekly" aria-label="weekly">
            Weekly
          </ToggleButton>
          <ToggleButton value="monthly" aria-label="monthly">
            Monthly
          </ToggleButton>
          <ToggleButton value="yearly" aria-label="yearly">
            Yearly
          </ToggleButton>
          <ToggleButton value="alltime" aria-label="all time">
            All Time
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Leaderboard Table */}
      <Paper elevation={3}>
        <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getMetricIcon(metric)}
              <Typography variant="h6">
                {getMetricLabel(metric)} - {getPeriodLabel(period)}
              </Typography>
            </Box>
            {!isAuthenticated && (
              <Chip
                label="Top 10"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
            )}
          </Box>
          {leaderboardData && leaderboardData.periodValue && period !== 'alltime' && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              Period: {leaderboardData.periodValue}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : leaderboardData && leaderboardData.leaderboard.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Rank</strong></TableCell>
                  <TableCell><strong>Player</strong></TableCell>
                  <TableCell align="right"><strong>Score</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardData.leaderboard.map((entry) => (
                  <TableRow
                    key={entry.userId}
                    sx={{
                      backgroundColor: entry.isCurrentUser ? 'action.selected' : 'inherit',
                      fontWeight: entry.isCurrentUser ? 'bold' : 'normal',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {entry.rank <= 3 && (
                          <EmojiEvents
                            sx={{
                              mr: 1,
                              color:
                                entry.rank === 1
                                  ? 'gold'
                                  : entry.rank === 2
                                  ? 'silver'
                                  : '#CD7F32',
                            }}
                          />
                        )}
                        <Typography variant="body1" sx={{ fontWeight: entry.rank <= 3 ? 'bold' : 'normal' }}>
                          {entry.rank}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                          {entry.displayName}
                        </Typography>
                        {entry.isCurrentUser && (
                          <Chip
                            label="You"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontWeight: entry.rank <= 3 ? 'bold' : 'normal' }}>
                        {formatValue(metric, entry.value)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4 }}>
            <Alert severity="info">
              No leaderboard data available for this period. Be the first to play and set a record!
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Sign in message for unauthenticated users */}
      {!isAuthenticated && leaderboardData && leaderboardData.leaderboard.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Sign in to see the full leaderboard and track your own ranking!
        </Alert>
      )}
    </Container>
  );
};

export default Leaderboard;
